//// filepath: c:\Users\evict\Desktop\SpellgatherV2\web\src\tour\TourProvider.tsx
//// Unified Joyride provider using steps.ts (with per-step route + wait + retry) + avatar auto-open/close and Spellgather/Bob branding.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Joyride, {
  ACTIONS,
  EVENTS,
  STATUS,
  CallBackProps,
  Step,
} from "react-joyride";
import { TOUR_STEPS } from "./steps";

/* ---------- Branding assets (display only) ---------- */
const SG_LOGO       = "/images/spellgather-logo.png";
const BOB_HAPPY     = "/images/bob-happy.png";
const BOB_POINT     = "/images/bob-point.png";
const BOB_THINKING  = "/images/bob-thinking.png";
const BOB_CELEBRATE = "/images/bob-celebrate.png";
const BOB_LOST      = "/images/lost-goblin.png";

/* -------- Types -------- */
interface RoutedStep extends Step {
  id: string;
  route: string;
  waitFor?: string;
  timeoutMs?: number;
}
type Ctx = {
  startTour: (fromId?: string) => void;
  stopTour: () => void;
  restartTour: () => void;
  running: boolean;
  stepId: string | null;
  // legacy aliases
  start: (fromId?: string) => void;
  stop: () => void;
  restart: () => void;
};

const TourContext = createContext<Ctx | null>(null);

/* -------- Utilities -------- */
async function waitForSelector(
  sel: string,
  timeout = 6000,
  poll = 120,
): Promise<boolean> {
  const t0 = performance.now();
  while (performance.now() - t0 < timeout) {
    if (document.querySelector(sel)) return true;
    await new Promise((r) => setTimeout(r, poll));
  }
  return false;
}

function toRoutedSteps(): RoutedStep[] {
  return TOUR_STEPS.map((s) => ({
    id: s.id,
    route: s.route,
    target: s.selector,
    title: s.title,
    content: s.body,
    placement: s.placement as any,
    waitFor: s.selector !== "body" ? s.selector : undefined,
  }));
}

/* ---------- Avatar menu helpers (side effects only; tour logic unchanged) ---------- */
const AVATAR_TRIGGER_SELS = [
  '[data-avatar="menu"]',
  '[data-avatar="menu-trigger"]',
  '[data-testid="avatar-menu"]',
  '[data-testid="avatar-trigger"]',
  "#avatarMenu",
  "#avatarMenuButton",
  'button[aria-haspopup="menu"][data-avatar="1"]',
];
const AVATAR_MENU_SELS = [
  '[role="menu"][data-avatar-menu="1"]',
  '[data-avatar="menu-panel"]',
  '[data-testid="avatar-menu-panel"]',
  "#avatarMenuPanel",
];
const q1 = (sels: string[]) => sels.map((s) => document.querySelector(s)).find(Boolean) || null;
const findAvatarTrigger = () => q1(AVATAR_TRIGGER_SELS);
const findAvatarMenu = () => q1(AVATAR_MENU_SELS);

function ensureAvatarMenuOpen(): boolean {
  try {
    if (findAvatarMenu()) return true;
    const t = findAvatarTrigger();
    if (!t) return false;
    // Try hover-open
    t.dispatchEvent(new Event("pointerenter", { bubbles: true }));
    t.dispatchEvent(new Event("mouseenter", { bubbles: true }));
    if (findAvatarMenu()) return true;
    // Try click-open
    (t as HTMLElement).click?.();
    if (findAvatarMenu()) return true;
    // Try keyboard-open
    (t as HTMLElement).focus?.();
    t.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter", bubbles: true }));
    return !!findAvatarMenu();
  } catch {
    return false;
  }
}
function ensureAvatarMenuClosed() {
  try {
    const m = findAvatarMenu();
    if (!m) return;
    const t = findAvatarTrigger();
    if (t) {
      t.dispatchEvent(new Event("pointerleave", { bubbles: true }));
      t.dispatchEvent(new Event("mouseleave", { bubbles: true }));
    }
    m.dispatchEvent(new Event("pointerleave", { bubbles: true }));
    m.dispatchEvent(new Event("mouseleave", { bubbles: true }));
    if (findAvatarMenu()) {
      document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
      (document.body as HTMLElement).click?.();
    }
  } catch {}
}
const isAvatarSelector = (sel?: string | null) => {
  if (!sel) return false;
  const s = String(sel);
  return (
    s.includes('[data-avatar="menu"]') ||
    s.includes('[data-tour="avatar-') ||
    s.includes('[data-testid="avatar-menu"]') ||
    s.includes("#avatarMenu") ||
    s.includes('[role="menu"][data-avatar-menu="1"]')
  );
};
const isAvatarStepId = (id?: string | null) => !!id && id.startsWith("avatar-");

/* ---------- Bob face (display only) ---------- */
type Mascot = "happy" | "point" | "thinking" | "celebrate" | "lost";
const MASCOT_IMG: Record<Mascot, string> = {
  happy: BOB_HAPPY,
  point: BOB_POINT,
  thinking: BOB_THINKING,
  celebrate: BOB_CELEBRATE,
  lost: BOB_LOST,
};
const idMatch = (id: string, pats: RegExp[]) => pats.some((r) => r.test(id));
const pickMascot = (id: string): Mascot => {
  if (id === "welcome") return "happy";
  if (/(finish|done|complete)$/.test(id)) return "celebrate";
  if (idMatch(id, [
    /dashboard-/,
    /marketplace-(search|filters|listings|community)/,
    /collection-(grid|import|scan|status|search)/,
    /trades-(send|inbox|sent|received|footer)/,
    /avatar-(share|trade-toggle|play-toggle|tour-button|bug-report|news-button|legal)/,
  ])) return "point";
  if (idMatch(id, [/global-search/, /wishlist/, /highlights|alerts|playopps/])) return "thinking";
  return "happy";
};

/* ---------- Custom tooltip (visual only; doesn’t change flow) ---------- */
type TooltipProps = Parameters<
  NonNullable<React.ComponentProps<typeof Joyride>["tooltipComponent"]>
>[0];

function SGTooltip({
  index,
  size,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
}: TooltipProps) {
  const stepId = String((step as any)?.id ?? "");
  const face = pickMascot(stepId);
  const imgSrc = MASCOT_IMG[face];

  let helper: React.ReactNode = null;
  if (stepId === "welcome")
    helper = <span className="text-[#A5B4FC]">Quick tour. Tap “Skip” anytime.</span>;
  else if (/avatar-/.test(stepId))
    helper = <span className="text-[#93C5FD]">We’ll open the profile menu for you. If it doesn’t open, tap your avatar.</span>;
  else if (/global-search|marketplace-search/.test(stepId))
    helper = <span className="text-[#7DD3FC]">Supports Scryfall queries.</span>;
  else if (/(finish|done|complete)$/.test(stepId))
    helper = <span className="text-[#86EFAC]">You can re-run this tour anytime from the avatar menu.</span>;

  return (
    <div
      {...tooltipProps}
      style={{
        ...tooltipProps.style,
        background: "#0B1020",
        border: "1px solid #2A2F45",
        borderRadius: 14,
        maxWidth: 460,
        boxShadow: "0 12px 32px rgba(0,0,0,.55)",
        color: "#E6E8EE",
        padding: 14,
      }}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 rounded-lg overflow-hidden border border-[#2A2F45]" style={{ width: 56, height: 56, background: "#0A0F1A" }}>
          <img src={imgSrc} alt="Bob" style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[13px] tracking-wide uppercase text-[#8B5CF6]">Spellgather Guide</div>
            <img src={SG_LOGO} alt="Spellgather" style={{ height: 20 }} onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
          </div>

          {step.title ? (
            <div className="text-[16px] font-semibold text-[#60A5FA] leading-tight mb-1">
              {step.title as React.ReactNode}
            </div>
          ) : null}

          <div className="text-[14px] text-[#D6D9E6] leading-snug">
            {step.content as React.ReactNode}
          </div>

          {helper ? <div className="mt-1 text-[12px]">{helper}</div> : null}

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-slate-400">Step {index + 1} / {size}</div>
            <div className="flex items-center gap-2">
              <button {...skipProps} className="px-3 py-1.5 rounded-md border border-[#334155] text-slate-200 hover:bg-[#11162a]">Skip</button>
              <button {...backProps} className="px-3 py-1.5 rounded-md border border-[#334155] text-slate-200 hover:bg-[#11162a] disabled:opacity-40">Back</button>
              <button {...primaryProps} className="px-3 py-1.5 rounded-md bg-gradient-to-r from-[#8B5CF6] via-[#6366F1] to-[#22D3EE] text-white hover:opacity-90">
                {(primaryProps as any)?.title || "Next"}
              </button>
              <button {...closeProps} className="px-2 py-1.5 rounded-md border border-[#334155] text-slate-200 hover:bg-[#11162a]" aria-label="Close" title="Close">✕</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------- Provider -------- */
export function TourProvider({
  children,
  autoStart,
  shouldStartNow,
  userId,
}: {
  children: ReactNode;
  autoStart?: boolean;
  shouldStartNow?: boolean;
  userId?: string | null;
}) {
  const navigate = useNavigate();
  const loc = useLocation();

  const stepsRef = useRef<RoutedStep[]>(toRoutedSteps());
  const [stepIndex, setStepIndex] = useState(0);
  const [run, setRun] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const tokenRef = useRef(0);
  const attemptsRef = useRef<Record<string, number>>({});
  const startedRef = useRef(false);
  const retryTimersRef = useRef<Record<string, number>>({});

  // Only a UI hint; flow is unchanged. Shown if auto-open fails.
  const [showAvatarNudge, setShowAvatarNudge] = useState(false);

  // Autostart via flags (UNCHANGED)
  useEffect(() => {
    let pending = false;
    try {
      if (localStorage.getItem("sg:pendingTour") === "1") {
        pending = true;
        localStorage.removeItem("sg:pendingTour");
      }
    } catch {}
    if ((autoStart || shouldStartNow || pending) && !startedRef.current) {
      startedRef.current = true;
      startTour();
    }
  }, [autoStart, shouldStartNow]);

  const normPath = (p: string) => (p ? p.split("?")[0].replace(/\/+$/, "") || "/" : "/").toLowerCase();
  const sameRoute = (a: string, b: string) => normPath(a) === normPath(b);

  // startTour: use normalized comparison
  const startTour = useCallback((fromId?: string) => {
    const steps = stepsRef.current;
    attemptsRef.current = {};
    let idx = 0;
    if (fromId) {
      const found = steps.findIndex((s) => s.id === fromId);
      if (found >= 0) idx = found;
    }
    setStepIndex(idx);

    const st = steps[idx];
    const needNav = !sameRoute(loc.pathname, st.route);
    const needWait = st.waitFor && !document.querySelector(st.waitFor);
    if (needNav || needWait) {
      setRun(false);
      setPreparing(true);
    } else {
      setRun(true);
      setPreparing(false);
    }
  }, [loc.pathname]);

  const stopTour = useCallback(() => {
    setRun(false);
    setPreparing(false);
    setShowAvatarNudge(false);
    // Extra cleanup: ensure menu is closed at the end
    ensureAvatarMenuClosed();
  }, []);

  const restartTour = useCallback(() => {
    startTour();
  }, [startTour]);

  // preparation effect: use normalized comparison
  useEffect(() => {
    if (!preparing) return;
    const steps = stepsRef.current;
    const step = steps[stepIndex];
    if (!step) { stopTour(); return; }
    const token = ++tokenRef.current;
    let cancelled = false;

    (async () => {
      setShowAvatarNudge(false);

      if (!sameRoute(loc.pathname, step.route)) {
        navigate(step.route);
        await new Promise((r) => setTimeout(r, 40));
        if (cancelled || tokenRef.current !== token) return;
      }

      // AUTO-OPEN avatar menu when a step targets it (fallback to small hint)
      const avatarish =
        isAvatarStepId(step.id) || isAvatarSelector(step.waitFor || (step.target as string));
      if (avatarish) {
        const opened = ensureAvatarMenuOpen();
        if (!opened) setShowAvatarNudge(true);
      } else {
        ensureAvatarMenuClosed(); // leaving avatar section: close proactively
      }

      const sel = step.waitFor;
      if (sel && !document.querySelector(sel)) {
        // If still waiting for avatar content, try opening again mid-wait
        if (avatarish) ensureAvatarMenuOpen();
        await waitForSelector(sel, step.timeoutMs ?? 5000, 120);
        if (cancelled || tokenRef.current !== token) return;
      } else {
        await new Promise((r) => setTimeout(r, 16));
      }

      if (cancelled || tokenRef.current !== token) return;
      setRun(true);
      setPreparing(false);
    })();

    return () => { cancelled = true; };
  }, [preparing, stepIndex, loc.pathname, navigate, stopTour]);

  // onCallback: treat last step TNF as finish instead of “fail”
  const onCallback = useCallback(
    (data: CallBackProps) => {
      const { type, action, index, status } = data;

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        stopTour();
        return;
      }

      const steps = stepsRef.current;

      const retrySameStep = (step: RoutedStep) => {
        const attempts = attemptsRef.current[step.id] || 0;
        if (attempts >= 2) {
          const nextIdx = index + 1;
          if (nextIdx >= steps.length) { // last step: gracefully finish
            stopTour();
            return;
          }
          const nextStep = steps[nextIdx];
          const leavingAvatar = !(isAvatarStepId(nextStep.id) || isAvatarSelector(nextStep.waitFor || (nextStep.target as string)));
          if (leavingAvatar) { ensureAvatarMenuClosed(); setShowAvatarNudge(false); }
          setRun(false);
          setStepIndex(nextIdx);
          setPreparing(true);
          return;
        }
        attemptsRef.current[step.id] = attempts + 1;
        if (isAvatarStepId(step.id) || isAvatarSelector(step.waitFor || (step.target as string))) {
          const opened = ensureAvatarMenuOpen();
          setShowAvatarNudge(!opened);
        }
        setRun(false);
        setPreparing(true);
      };

      if (type === EVENTS.TARGET_NOT_FOUND) {
        const step = steps[index];
        if (!step) { stopTour(); return; }
        // If this is the final “bob” step and target missing, finish cleanly
        if (index === steps.length - 1 || step.id === "bob") {
          stopTour();
          return;
        }
        if (isAvatarStepId(step.id) || isAvatarSelector(step.waitFor || (step.target as string))) {
          const opened = ensureAvatarMenuOpen();
          setShowAvatarNudge(!opened);
        }
        retrySameStep(step);
        return;
      }

      if (type === EVENTS.STEP_AFTER) {
        let nextIdx = index;
        if (action === ACTIONS.NEXT) nextIdx = index + 1;
        else if (action === ACTIONS.PREV) nextIdx = Math.max(0, index - 1);
        if (nextIdx === index) return;

        const nextStep = steps[nextIdx];
        if (!nextStep) { stopTour(); return; }

        const leavingAvatar = !(isAvatarStepId(nextStep.id) || isAvatarSelector(nextStep.waitFor || (nextStep.target as string)));
        if (leavingAvatar) { ensureAvatarMenuClosed(); setShowAvatarNudge(false); }

        const needNav = !sameRoute(loc.pathname, nextStep.route);
        const needWait = nextStep.waitFor && !document.querySelector(nextStep.waitFor);

        setStepIndex(nextIdx);
        if (needNav || needWait) { setRun(false); setPreparing(true); }
        else { setRun(true); setPreparing(false); }
      }
    },
    [loc.pathname, stopTour],
  );

  // (Optional) Dev logging – uncomment if needed:
  // useEffect(() => { console.log("[Tour] idx", stepIndex, stepsRef.current[stepIndex]?.id, { run, preparing, path: loc.pathname }); }, [stepIndex, run, preparing, loc.pathname]);

  const value: Ctx = useMemo(
    () => ({
      startTour,
      stopTour,
      restartTour,
      running: run,
      stepId: run ? stepsRef.current[stepIndex]?.id ?? null : null,
      start: startTour,
      stop: stopTour,
      restart: restartTour,
    }),
    [run, stepIndex, startTour, stopTour, restartTour],
  );

  return (
    <TourContext.Provider value={value}>
      {children}

      {/* Gentle avatar nudge (only if auto-open failed) */}
      {showAvatarNudge && (
        <div className="fixed top-3 right-3 z-[9998] rounded-md border border-[#334155] bg-[#0B1020]/90 px-3 py-2 text-xs text-slate-200 shadow">
          <div className="font-medium text-[#93C5FD] mb-0.5">Profile menu</div>
          <div>Tap your avatar to open the menu to continue.</div>
        </div>
      )}

      {preparing && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center pointer-events-none">
          <div className="px-3 py-1.5 text-xs rounded-md bg-black/70 text-emerald-300 border border-emerald-500/40 shadow">
            Preparing next step…
          </div>
        </div>
      )}

      <Joyride
        steps={stepsRef.current as Step[]}
        run={run}
        stepIndex={stepIndex}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        disableScrolling={false}
        callback={onCallback}
        tooltipComponent={SGTooltip}
        locale={{ back: "Back", close: "Close", last: "Done", next: "Next", skip: "Skip" }}
        styles={{
          options: {
            zIndex: 9999,
            primaryColor: "#8B5CF6",
            overlayColor: "rgba(0,0,0,0.58)",
            textColor: "#E5E7EB",
            arrowColor: "#0B1020",
            backgroundColor: "#0B1020",
          },
          tooltip: {
            backgroundColor: "#0f172a",
            borderRadius: 12,
            maxWidth: 460,
            padding: "16px 18px",
            border: "1px solid #2A2F45",
          },
          tooltipTitle: { color: "#60A5FA", fontSize: 16, fontWeight: 600 },
          buttonNext: { backgroundColor: "#6366F1", borderRadius: 8 },
          buttonBack: { color: "#93A0B0" },
          beacon: { boxShadow: "0 0 0 3px rgba(99,102,241,0.35)" },
        }}
        disableCloseOnEsc={false}
        disableOverlayClose={false}
      />
    </TourContext.Provider>
  );
}

/* -------- Hook -------- */
export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) {
    return {
      startTour: () => {},
      stopTour: () => {},
      restartTour: () => {},
      running: false,
      stepId: null,
      start: () => {},
      stop: () => {},
      restart: () => {},
    } as Ctx;
  }
  return ctx;
}

